import { LoadOptions } from 'devextreme/data';
import {
	FilterType,
	GroupOperator,
	ILoadFilterGroup,
	ILoadFilterItem
} from '../../BCExtremeGrid/entities';
import { BCExtremeGridEntities } from '../../BCExtremeGrid/Lazy';
import { Entities } from '../exports';

export const convertLoadOptions = (
	dxLoadOptions: LoadOptions & { isLoadingAll?: boolean },
	columns: Entities.IRemoteColumnProps[]
): BCExtremeGridEntities.ILoadOptions => {
	const sorts = convertSorts(dxLoadOptions.sort);
	const pagination = dxLoadOptions.isLoadingAll
		? convertPagination(9999, 0)
		: convertPagination(dxLoadOptions.take, dxLoadOptions.skip, dxLoadOptions);

	const getColumnDbType = (name: string): BCExtremeGridEntities.ItemType => {
		const column = columns.find(column => column.name === name || column.dataField === name);
		if (column?.dbType) return column.dbType;
		throw new Error(`Column not found: ${name}`);
	};

	const filters = convertFilter(dxLoadOptions.filter, getColumnDbType);
	return {
		sorts,
		pagination,
		filters
	};
};

const convertFilter = (
	filter: LoadOptions['filter'],
	getColumnDbType: (name: string) => BCExtremeGridEntities.ItemType
): Array<ILoadFilterItem | ILoadFilterGroup> => {
	const mapComparisonOperator = (operator: string): FilterType => {
		switch (operator) {
			case '=':
				return FilterType.Equal;
			case '>':
				return FilterType.Greater;
			case '>=':
				return FilterType.GreaterOrEqual;
			case '<':
				return FilterType.Less;
			case '<=':
				return FilterType.LessOrEqual;
			case 'contains':
				return FilterType.Contain;
			case 'startswith':
				return FilterType.StartsWith;
			case 'endswith':
				return FilterType.EndsWith;
			case 'notcontains':
				return FilterType.Contain;
			case '<>':
				return FilterType.Equal;
			default:
				throw new Error(`Unsupported comparison operator: ${operator}`);
		}
	};

	const mapLogicalOperator = (operator: string): GroupOperator => {
		switch (operator) {
			case 'and':
				return GroupOperator.And;
			case 'or':
				return GroupOperator.Or;
			default:
				throw new Error(`Unsupported logical operator: ${operator}`);
		}
	};

	const parseFilter = (filter, not: boolean = false): ILoadFilterItem | ILoadFilterGroup => {
		if (Array.isArray(filter)) {
			if (
				filter.length === 3 &&
				typeof filter[1] === 'string' &&
				filter[1] !== 'and' &&
				filter[1] !== 'or'
			) {
				let columnName = filter[0];
				if (typeof columnName === 'function') {
					// If we assign the calculateDisplayValue property to the column as a function, it arrives here as a function as well
					columnName = filter[0].context.column.dataField;
				}

				const filterOperator = filter[1];

				const gridFilterItem: ILoadFilterItem = {
					Column: columnName,
					Not: not || filterOperator === '<>' || filterOperator === 'notcontains',
					Type: mapComparisonOperator(filterOperator),
					Value: filter[2],
					ItemType: getColumnDbType(columnName)
				};

				return parseFilterValue(gridFilterItem);
			}

			if (filter.length === 2 && filter[0] === '!') {
				return parseFilter(filter[1], true);
			}

			const logicalOperators = ['and', 'or'];
			let operator: GroupOperator | null = null;
			const subFilters: Array<ILoadFilterItem | ILoadFilterGroup> = [];

			for (const item of filter) {
				if (typeof item === 'string' && logicalOperators.includes(item)) {
					const newOperator = mapLogicalOperator(item);
					if (operator && operator !== newOperator) {
						throw new Error(
							`Mixed logical operators are not supported: ${operator} and ${newOperator}`
						);
					}
					operator = newOperator;
				} else {
					subFilters.push(parseFilter(item, not));
				}
			}

			if (!operator) {
				throw new Error('No valid logical operator found in the group filter.');
			}

			return {
				Operator: not ? 'and' : operator,
				Filters: subFilters.filter(Boolean)
			} as ILoadFilterGroup;
		}

		throw new Error('Invalid filter format.');
	};

	return (Array.isArray(filter) ? [parseFilter(filter)] : []).filter(Boolean);
};

const convertSorts = (dxSort: any[] | any) => {
	const sorts: BCExtremeGridEntities.ISort[] = [];

	if (!dxSort) {
		return sorts;
	}

	if (Array.isArray(dxSort)) {
		dxSort.forEach(sort =>
			sorts.push({
				Column:
					typeof sort.selector === 'string'
						? sort.selector
						: sort.selector.context.column.dataField,
				Type: sort.desc
					? BCExtremeGridEntities.SortOrder.Desc
					: BCExtremeGridEntities.SortOrder.Asc
			})
		);
	} else {
		sorts.push({
			Column:
				typeof dxSort.selector === 'string'
					? dxSort.selector
					: dxSort.selector.context.column.dataField,
			Type: dxSort.desc
				? BCExtremeGridEntities.SortOrder.Desc
				: BCExtremeGridEntities.SortOrder.Asc
		});
	}

	return sorts;
};

const convertPagination = (take: number, skip: number, loadOptions?) => {
	let pagination: BCExtremeGridEntities.IPagination = {
		skip: skip || 0,
		take: take || 10
	};

	if (
		loadOptions &&
		loadOptions.hasOwnProperty('select') &&
		loadOptions.hasOwnProperty('filter')
	) {
		pagination.skip = 0;
		pagination.take = loadOptions.filter.length - (loadOptions.filter.length - 1) / 2;
	}

	return pagination;
};

const parseFilterValue = (filterItem: ILoadFilterItem): ILoadFilterItem => {
	if (filterItem.ItemType === BCExtremeGridEntities.ItemType.Decimal) {
		const value = parseFloat(filterItem.Value);

		if (isNaN(value)) {
			return null;
		}

		filterItem.Value = value;
	}

	return filterItem;
};
